import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  message,
  Typography,
  Space,
  Tag,
  Button,
  Modal,
  List,
  Badge,
  Upload,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  PaperClipOutlined,
  EyeOutlined,
  DownloadOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import FilePreview from '../Case/components/shared/FilePreview';
import LeadDrawer from '../../components/LeadDrawer';
import CaseDrawer from '../../components/CaseDrawer';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { logError } from '../../utils/logger';
import { getToken, getUserInfo } from '../../utils/auth';
import {
  getAllFinanceRecords,
  FinanceRecordWithDetails,
  ExpenseType,
  PaymentNature,
  GetAllFinanceRecordsParams,
  EntityType,
  getFinanceStatistics,
  FinanceStatistics as FinanceStatisticsType,
} from '../../services/finance';
import FinanceStatistics from './components/FinanceStatistics';
import FinanceTableEnhanced from './components/FinanceTableEnhanced';
import FinanceCardView from './components/FinanceCardView';
import { useResponsive } from '../../hooks/useResponsive';
import './styles.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// 费用类型映射
const EXPENSE_TYPE_MAP: Record<ExpenseType, string> = {
  [ExpenseType.CONSULTATION_FEE]: '咨询费',
  [ExpenseType.LAWYER_FEE]: '律师费',
  [ExpenseType.CONSULTANT_FEE]: '顾问费',
  [ExpenseType.SERVICE_FEE]: '服务费',
  [ExpenseType.TRAVEL_FEE]: '差旅费',
  [ExpenseType.OTHER]: '其他',
};

// 款项类型映射
const PAYMENT_NATURE_MAP: Record<PaymentNature, string> = {
  [PaymentNature.RECEIVED]: '已收款',
  [PaymentNature.RECEIVABLE]: '应收款',
  [PaymentNature.REFUND]: '退款',
};

// 实体类型映射
const ENTITY_TYPE_MAP: Record<EntityType, string> = {
  [EntityType.LEAD]: '线索',
  [EntityType.CASE]: '案件',
};

const FinanceManagement: React.FC = () => {
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<FinanceRecordWithDetails[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // 获取当前用户信息
  const currentUser = getUserInfo();

  // 统计数据
  const [statistics, setStatistics] = useState<FinanceStatisticsType | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);

  // 搜索和筛选参数
  const [caseName, setCaseName] = useState<string>();
  const [contactName, setContactName] = useState<string>();
  const [contactPhone, setContactPhone] = useState<string>();
  const [expenseType, setExpenseType] = useState<ExpenseType>();
  const [paymentNature, setPaymentNature] = useState<PaymentNature>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // 附件相关状态
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<any[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  // 详情抽屉状态
  const [leadDrawerVisible, setLeadDrawerVisible] = useState(false);
  const [caseDrawerVisible, setCaseDrawerVisible] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  // 上传发票状态
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<FinanceRecordWithDetails | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadStatistics();
    loadRecords();
  }, [page, pageSize]);

  const loadStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const data = await getFinanceStatistics();
      setStatistics(data);
    } catch (error) {
      logError('Failed to load finance statistics', error);
      message.error('加载统计数据失败');
    } finally {
      setStatisticsLoading(false);
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params: GetAllFinanceRecordsParams = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };

      if (caseName) params.case_name = caseName;
      if (contactName) params.contact_name = contactName;
      if (contactPhone) params.contact_phone = contactPhone;
      if (expenseType) params.expense_type = expenseType;
      if (paymentNature) params.payment_nature = paymentNature;
      if (dateRange) {
        params.payment_time_start = dateRange[0].format('YYYY-MM-DD HH:mm:ss');
        params.payment_time_end = dateRange[1].format('YYYY-MM-DD HH:mm:ss');
      }

      const response = await getAllFinanceRecords(params);
      setRecords(response.items);
      setTotal(response.total);
    } catch (error) {
      logError('Failed to load finance records', error);
      message.error('加载财务记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const handleViewEntity = (entityType: EntityType, entityId: number) => {
    // 根据实体类型打开对应的详情抽屉
    if (entityType === EntityType.LEAD) {
      setSelectedLeadId(entityId);
      setLeadDrawerVisible(true);
    } else if (entityType === EntityType.CASE) {
      setSelectedCaseId(entityId);
      setCaseDrawerVisible(true);
    }
  };

  const handleReset = () => {
    setCaseName(undefined);
    setContactName(undefined);
    setContactPhone(undefined);
    setExpenseType(undefined);
    setPaymentNature(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => loadRecords(), 0);
  };

  /**
   * Handle export to Excel
   */
  const handleExport = async () => {
    if (records.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    try {
      // 动态导入 xlsx 以避免 TypeScript 编译错误
      // @ts-ignore
      const XLSX = await import('xlsx');
      
      // 准备导出数据
      const exportData = records.map(record => {
        const isLead = record.entity_type === EntityType.LEAD;
        return {
          '案件名称': record.case_name || record.contact_name || '-',
          '联系人': record.contact_name || '-',
          '案由': (record as any).case_reason || '-',
          '费用类型': EXPENSE_TYPE_MAP[record.expense_type as ExpenseType] || record.expense_type,
          '款项类型': PAYMENT_NATURE_MAP[record.payment_nature as PaymentNature] || record.payment_nature,
          '动款时间': record.payment_time ? dayjs(record.payment_time).format('YYYY-MM-DD HH:mm:ss') : '-',
          '动款金额': record.amount || 0,
          '接案老师': isLead ? (record.responsible_person_name || '-') : ((record as any).lead_assignee_name || '-'),
          '办案律师': !isLead ? (record.responsible_person_name || '-') : '-',
        };
      });

      // 创建工作簿
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '财务记录');

      // 设置列宽
      const colWidths = [
        { wch: 30 }, // 案件名称
        { wch: 15 }, // 联系人
        { wch: 20 }, // 案由
        { wch: 12 }, // 费用类型
        { wch: 12 }, // 款项类型
        { wch: 20 }, // 动款时间
        { wch: 12 }, // 动款金额
        { wch: 15 }, // 接案老师
        { wch: 15 }, // 办案律师
      ];
      worksheet['!cols'] = colWidths;

      // 生成文件名
      const fileName = `${dayjs().format('YYYY-MM-DD')}_浙江同济财务记录.xlsx`;

      // 导出文件
      XLSX.writeFile(workbook, fileName);
      message.success(`成功导出 ${records.length} 条记录`);
    } catch (error) {
      logError('Failed to export finance records', error);
      message.error('导出失败，请重试');
    }
  };

  /**
   * Handle upload invoice
   */
  const handleUploadInvoice = (record: FinanceRecordWithDetails) => {
    setSelectedRecord(record);
    setUploadModalVisible(true);
  };

  /**
   * Handle invoice file upload
   */
  const handleInvoiceUpload = async (file: File) => {
    if (!selectedRecord) return;

    const recordId = selectedRecord.id;
    setUploading(true);
    try {
      const token = getToken();
      if (!token) {
        throw new Error('未登录，请先登录');
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/v1/finance/records/${recordId}/invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '上传失败');
      }

      const result = await response.json();
      message.success('发票上传成功');
      
      // 将新上传的发票添加到selectedRecord的invoices中
      if (selectedRecord.invoices) {
        setSelectedRecord({
          ...selectedRecord,
          invoices: [...selectedRecord.invoices, result.attachment]
        });
      } else {
        setSelectedRecord({
          ...selectedRecord,
          invoices: [result.attachment]
        });
      }
      
      // 刷新列表数据（后台刷新，不影响Modal显示）
      loadRecords();
    } catch (error: any) {
      logError('Failed to upload invoice', error);
      message.error(error.message || '上传发票失败');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle delete invoice
   */
  const handleDeleteInvoice = async (attachmentId: number) => {
    if (!selectedRecord) return;

    try {
      const token = getToken();
      if (!token) {
        throw new Error('未登录，请先登录');
      }

      const response = await fetch(`/api/v1/finance/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '删除失败');
      }

      message.success('发票删除成功');
      
      // 从selectedRecord的invoices中移除已删除的发票
      if (selectedRecord.invoices) {
        setSelectedRecord({
          ...selectedRecord,
          invoices: selectedRecord.invoices.filter(inv => inv.id !== attachmentId)
        });
      }
      
      // 刷新列表数据（后台刷新）
      loadRecords();
    } catch (error: any) {
      logError('Failed to delete invoice', error);
      message.error(error.message || '删除发票失败');
    }
  };

  const getPaymentNatureColor = (nature: PaymentNature) => {
    const colorMap: Record<PaymentNature, string> = {
      [PaymentNature.RECEIVED]: 'green',
      [PaymentNature.RECEIVABLE]: 'orange',
      [PaymentNature.REFUND]: 'red',
    };
    return colorMap[nature] || 'default';
  };

  /**
   * Handle view attachments
   */
  const handleViewAttachments = (attachments: any[]) => {
    setSelectedAttachments(attachments);
    setAttachmentModalVisible(true);
  };

  /**
   * Handle preview attachment
   */
  const handlePreviewAttachment = async (attachment: any) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('未登录，请先登录');
      }

      // 获取临时预览URL
      const response = await fetch(`/api/v1/finance/attachments/${attachment.id}/download?inline=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('获取预览链接失败');
      }

      const data = await response.json();

      setPreviewFile({
        file_url: data.url,
        original_filename: data.file_name,
        content_type: data.file_type,
      });
      setPreviewVisible(true);
      setAttachmentModalVisible(false);
    } catch (error) {
      console.error('Failed to get attachment URL:', error);
      message.error('获取附件链接失败');
    }
  };

  /**
   * Handle download attachment
   */
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('未登录，请先登录');
      }

      // 获取临时下载URL
      const response = await fetch(`/api/v1/finance/attachments/${attachment.id}/download?inline=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('获取下载链接失败');
      }

      const data = await response.json();

      // 在新窗口打开下载链接
      window.open(data.url, '_blank');
      message.success('开始下载');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      message.error('下载失败');
    }
  };

  /**
   * Render attachment count badge
   */
  const renderAttachments = (attachments: any[]): React.ReactNode => {
    if (!attachments || attachments.length === 0) {
      return <Text type="secondary">-</Text>;
    }

    return (
      <div
        style={{ cursor: 'pointer', display: 'inline-block' }}
        onClick={(e) => {
          e.stopPropagation();
          handleViewAttachments(attachments);
        }}
      >
        <Badge count={attachments.length} showZero={false}>
          <PaperClipOutlined style={{ fontSize: 16 }} />
        </Badge>
      </div>
    );
  };

  const columns: ColumnsType<FinanceRecordWithDetails> = [
    {
      title: 'ID',
      key: 'index',
      width: 80,
      fixed: 'left',
      render: (_: any, __: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: '联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 120,
      render: (name) => name || '-',
    },
    {
      title: '案件名称',
      dataIndex: 'case_name',
      key: 'case_name',
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (name, record) => (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <Text ellipsis={{ tooltip: name }} style={{ maxWidth: '100%' }}>
            {name || '-'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ENTITY_TYPE_MAP[record.entity_type]}
          </Text>
        </Space>
      ),
    },
    {
      title: '费用类型',
      dataIndex: 'expense_type',
      key: 'expense_type',
      width: 120,
      render: (type: ExpenseType) => EXPENSE_TYPE_MAP[type] || type,
    },
    {
      title: '动款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: string | number, record) => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        const color = record.payment_nature === PaymentNature.REFUND ? '#ff4d4f' : '#52c41a';
        const prefix = record.payment_nature === PaymentNature.REFUND ? '-' : '+';
        return (
          <Text strong style={{ color }}>
            {prefix}￥{numAmount.toLocaleString()}
          </Text>
        );
      },
      sorter: (a, b) => {
        const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
        const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
        return amountA - amountB;
      },
    },
    {
      title: '动款时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a, b) => {
        if (!a.payment_time) return 1;
        if (!b.payment_time) return -1;
        return dayjs(a.payment_time).unix() - dayjs(b.payment_time).unix();
      },
    },
    {
      title: '款项类型',
      dataIndex: 'payment_nature',
      key: 'payment_nature',
      width: 100,
      render: (nature: PaymentNature) => (
        <Tag color={getPaymentNatureColor(nature)}>
          {PAYMENT_NATURE_MAP[nature] || nature}
        </Tag>
      ),
    },
    {
      title: '发票',
      dataIndex: 'invoices',
      key: 'invoices',
      width: 80,
      align: 'center',
      render: (invoices: any[]) => renderAttachments(invoices),
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 80,
      align: 'center',
      render: (attachments: any[]) => renderAttachments(attachments),
    },
    {
      title: '线索/案件负责人',
      dataIndex: 'responsible_person_name',
      key: 'responsible_person_name',
      width: 120,
      render: (name) => name || '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
      render: (remark) => remark || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a, b) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return dayjs(a.created_at).unix() - dayjs(b.created_at).unix();
      },
      defaultSortOrder: 'descend',
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_: any, record: FinanceRecordWithDetails) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => handleUploadInvoice(record)}
          >
            上传发票
          </Button>
          {/* 只有管理员可以查看详情 */}
          {currentUser?.role === 'admin' && record.entity_id && (
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewEntity(record.entity_type, record.entity_id)}
            >
              查看详情
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="fade-in">
      {/* 统计指标 */}
      <FinanceStatistics statistics={statistics} loading={statisticsLoading} />

      {/* 搜索和筛选 */}
      <Card style={{ marginBottom: 16 }}>
        {/* 筛选条件 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {/* 第一行 */}
          <div>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              案件名称
            </div>
            <Input
              placeholder="搜索案件名称"
              prefix={<SearchOutlined />}
              value={caseName}
              onChange={(e) => setCaseName(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              联系人名称
            </div>
            <Input
              placeholder="搜索联系人名称"
              prefix={<SearchOutlined />}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              联系人手机号
            </div>
            <Input
              placeholder="搜索联系人手机号"
              prefix={<SearchOutlined />}
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </div>

          <div>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              费用类型
            </div>
            <Select
              placeholder="选择费用类型"
              value={expenseType}
              onChange={setExpenseType}
              style={{ width: '100%' }}
              allowClear
            >
              {Object.entries(EXPENSE_TYPE_MAP).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>

          {/* 第二行 */}
          <div>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              款项类型
            </div>
            <Select
              placeholder="选择款项类型"
              value={paymentNature}
              onChange={setPaymentNature}
              style={{ width: '100%' }}
              allowClear
            >
              {Object.entries(PAYMENT_NATURE_MAP).map(([key, value]) => (
                <Option key={key} value={key}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ marginBottom: 8, fontSize: 14, color: 'rgba(0, 0, 0, 0.85)' }}>
              动款时间
            </div>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              placeholder={['开始时间', '结束时间']}
              style={{ width: '100%' }}
              showTime
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                搜索
              </Button>
              <Button
                icon={<FilterOutlined />}
                onClick={handleReset}
              >
                重置
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={records.length === 0}
              >
                导出
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* 财务记录列表 */}
      <Card className="table-container">
        {isMobile ? (
          <FinanceCardView
            records={records}
            loading={loading}
            onViewEntity={currentUser?.role === 'admin' ? handleViewEntity : undefined}
          />
        ) : (
          <FinanceTableEnhanced
            records={records}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={(page, size) => {
              setPage(page);
              setPageSize(size || 50);
            }}
            onUploadInvoice={handleUploadInvoice}
            onViewEntity={handleViewEntity}
            renderAttachments={renderAttachments}
            currentUserRole={currentUser?.role}
          />
        )}
      </Card>

      {/* 上传发票弹窗 */}
      <Modal
        title="上传发票"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={600}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>财务记录信息：</Text>
              <div style={{ marginTop: 8 }}>
                <Text>联系人：{selectedRecord.contact_name || '-'}</Text>
                <br />
                <Text>案件名称：{selectedRecord.case_name || '-'}</Text>
                <br />
                <Text>费用类型：{EXPENSE_TYPE_MAP[selectedRecord.expense_type]}</Text>
                <br />
                <Text>金额：￥{parseFloat(selectedRecord.amount as any).toLocaleString()}</Text>
              </div>
            </div>

            {/* 已上传的发票列表 */}
            {selectedRecord.invoices && selectedRecord.invoices.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>已上传的发票：</Text>
                <List
                  size="small"
                  dataSource={selectedRecord.invoices}
                  renderItem={(invoice: any) => (
                    <List.Item
                      actions={[
                        <Button
                          key="preview"
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => handlePreviewAttachment(invoice)}
                        >
                          预览
                        </Button>,
                        <Button
                          key="download"
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment(invoice)}
                        >
                          下载
                        </Button>,
                        <Popconfirm
                          key="delete"
                          title="确定要删除这个发票吗？"
                          onConfirm={() => handleDeleteInvoice(invoice.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          >
                            删除
                          </Button>
                        </Popconfirm>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<PaperClipOutlined />}
                        title={invoice.file_name}
                        description={`${(invoice.file_size / 1024).toFixed(2)} KB`}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* 上传组件 */}
            <Upload.Dragger
              name="file"
              multiple={false}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              beforeUpload={(file) => {
                handleInvoiceUpload(file);
                return false; // 阻止自动上传
              }}
              showUploadList={false}
              disabled={uploading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持格式：PDF、图片（JPG/PNG）、Word、Excel
                <br />
                文件大小不超过 20MB
              </p>
            </Upload.Dragger>
          </div>
        )}
      </Modal>

      {/* 附件列表弹窗 */}
      <Modal
        title="附件列表"
        open={attachmentModalVisible}
        onCancel={() => setAttachmentModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={selectedAttachments}
          renderItem={(attachment: any) => (
            <List.Item
              actions={[
                <Button
                  key="preview"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreviewAttachment(attachment)}
                >
                  预览
                </Button>,
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadAttachment(attachment)}
                >
                  下载
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ fontSize: 20 }} />}
                title={attachment.file_name}
                description={`大小: ${attachment.file_size ? (attachment.file_size / 1024).toFixed(2) + ' KB' : '未知'}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 文件预览弹窗 */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
            setAttachmentModalVisible(true);
          }}
          onDownload={() => {
            const attachment = selectedAttachments.find(
              att => att.file_name === previewFile.original_filename
            );
            if (attachment) {
              handleDownloadAttachment(attachment);
            }
          }}
        />
      )}

      {/* 线索详情抽屉 */}
      <LeadDrawer
        leadId={selectedLeadId}
        visible={leadDrawerVisible}
        onClose={() => {
          setLeadDrawerVisible(false);
          setSelectedLeadId(null);
        }}
        onUpdate={() => {
          // 可选：刷新财务记录列表
          loadRecords();
        }}
      />

      {/* 案件详情抽屉 */}
      <CaseDrawer
        caseId={selectedCaseId}
        visible={caseDrawerVisible}
        onClose={() => {
          setCaseDrawerVisible(false);
          setSelectedCaseId(null);
        }}
        onUpdate={() => {
          // 可选：刷新财务记录列表
          loadRecords();
        }}
      />
    </div>
  );
};

export default FinanceManagement;
